import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DueCollectionItem {
  id: string;
  collection_date: string;
  invoice_number: string;
  patient_name: string;
  patient_unique_id: string;
  previous_due: number;
  collected_amount: number;
  remaining_due: number;
  payment_method: string;
  collected_by: string;
}

export default function DueCollectionReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<DueCollectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadDueCollectionReport();
  }, [startDate, endDate]);

  const loadDueCollectionReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockDueCollectionReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('payments')
        .select(`
          id,
          payment_date,
          amount,
          payment_method,
          created_by,
          invoice:invoices(
            invoice_number,
            total,
            amount_paid,
            balance_due,
            patient:patients(name, unique_id)
          )
        `)
        .gt('invoice.balance_due', 0);

      if (startDate) {
        query = query.gte('payment_date', startDate);
      }
      if (endDate) {
        query = query.lte('payment_date', endDate);
      }

      const { data, error } = await query.order('payment_date', { ascending: false });
      if (error) throw error;

      const formattedData: DueCollectionItem[] = (data || []).map(item => ({
        id: item.id,
        collection_date: item.payment_date,
        invoice_number: item.invoice?.invoice_number || 'N/A',
        patient_name: item.invoice?.patient?.name || 'N/A',
        patient_unique_id: item.invoice?.patient?.unique_id || 'N/A',
        previous_due: (item.invoice?.balance_due || 0) + item.amount,
        collected_amount: item.amount,
        remaining_due: item.invoice?.balance_due || 0,
        payment_method: item.payment_method,
        collected_by: item.created_by || 'N/A',
      }));

      setReportData(formattedData);
    } catch (error) {
      console.error('Error loading due collection report:', error);
      setReportData(getMockDueCollectionReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockDueCollectionReport = (): DueCollectionItem[] => {
    return [
      {
        id: '1',
        collection_date: '2025-01-27',
        invoice_number: 'INV-2025-001',
        patient_name: 'John Doe',
        patient_unique_id: 'P-001',
        previous_due: 5000,
        collected_amount: 2000,
        remaining_due: 3000,
        payment_method: 'cash',
        collected_by: 'Receptionist 1',
      },
      {
        id: '2',
        collection_date: '2025-01-26',
        invoice_number: 'INV-2025-002',
        patient_name: 'Jane Smith',
        patient_unique_id: 'P-002',
        previous_due: 7500,
        collected_amount: 3000,
        remaining_due: 4500,
        payment_method: 'card',
        collected_by: 'Receptionist 2',
      },
      {
        id: '3',
        collection_date: '2025-01-25',
        invoice_number: 'INV-2025-003',
        patient_name: 'Ahmed Khan',
        patient_unique_id: 'P-003',
        previous_due: 4000,
        collected_amount: 4000,
        remaining_due: 0,
        payment_method: 'cash',
        collected_by: 'Receptionist 1',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_unique_id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalCollected = filteredData.reduce((sum, item) => sum + item.collected_amount, 0);
  const totalRemaining = filteredData.reduce((sum, item) => sum + item.remaining_due, 0);

  const handleExportCSV = () => {
    const headers = ['Date', 'Invoice #', 'Patient ID', 'Patient Name', 'Previous Due', 'Collected', 'Remaining Due', 'Method', 'Collected By'];
    const rows = filteredData.map(item => [
      item.collection_date,
      item.invoice_number,
      item.patient_unique_id,
      item.patient_name,
      item.previous_due,
      item.collected_amount,
      item.remaining_due,
      item.payment_method,
      item.collected_by,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `due-collection-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Due Collection Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Collections</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredData.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Collected</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalCollected)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Remaining Due</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalRemaining)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search invoice, patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Previous Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Collected
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remaining Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Collected By
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                      No due collections found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.collection_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{item.patient_name}</div>
                          <div className="text-gray-500 text-xs">{item.patient_unique_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.previous_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                        {formatCurrency(item.collected_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                        {formatCurrency(item.remaining_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.payment_method === 'cash' ? 'bg-green-100 text-green-800' :
                          item.payment_method === 'card' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {item.payment_method}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.collected_by}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
